import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Home, LogOut, Building2, TrendingUp, Users, BarChart3, Phone } from 'lucide-react';

export default function LandlordDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="landlord-dashboard">
      <div className="bg-white border-b border-stone-200 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <h1 className="font-outfit font-bold text-stone-900 text-lg">Landlord Portal</h1>
            <Badge className="bg-blue-100 text-blue-800 text-xs">Landlord</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="landlord-home-btn">
              <Home className="w-4 h-4 mr-1" /> Home
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="landlord-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="font-outfit text-2xl font-bold text-stone-900">Welcome, {user?.name || 'Landlord'}</h2>
          <p className="text-stone-600 mt-1">Manage your properties and view performance reports.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="landlord-properties-card">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-emerald-900" />
            </div>
            <h3 className="font-outfit font-semibold text-stone-900 mb-2">Properties</h3>
            <p className="text-stone-600 text-sm">View and manage your property portfolio.</p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="landlord-financials-card">
            <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-amber-700" />
            </div>
            <h3 className="font-outfit font-semibold text-stone-900 mb-2">Financial Reports</h3>
            <p className="text-stone-600 text-sm">Revenue, expenses, and ROI tracking.</p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="landlord-tenants-card">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-blue-700" />
            </div>
            <h3 className="font-outfit font-semibold text-stone-900 mb-2">Tenants</h3>
            <p className="text-stone-600 text-sm">View tenant info and communications.</p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="landlord-analytics-card">
            <div className="w-12 h-12 bg-purple-50 rounded-2xl flex items-center justify-center mb-4">
              <BarChart3 className="w-6 h-6 text-purple-700" />
            </div>
            <h3 className="font-outfit font-semibold text-stone-900 mb-2">Analytics</h3>
            <p className="text-stone-600 text-sm">Market data and property analytics.</p>
          </div>
        </div>

        <div className="mt-10 bg-white rounded-2xl border border-stone-200 p-8 shadow-sm" data-testid="landlord-contact-section">
          <h3 className="font-outfit font-semibold text-stone-900 text-lg mb-4">Your Account Manager</h3>
          <p className="text-stone-600 text-sm mb-4">Have questions about your properties? Reach out to our dedicated team.</p>
          <div className="flex flex-wrap gap-4">
            <a href="tel:1-888-419-9767" className="flex items-center gap-2 text-emerald-900 font-medium text-sm hover:underline">
              <Phone className="w-4 h-4" /> 1-888-419-9767
            </a>
            <a href="mailto:admin@4longevity.live" className="text-emerald-900 font-medium text-sm hover:underline">admin@4longevity.live</a>
          </div>
        </div>
      </div>
    </div>
  );
}
